import { QueryClient } from '@tanstack/react-query'

/**
 * React Query Client Configuration for IoAg WebApp
 *
 * Two-layer caching architecture:
 * 1. Application Layer (React Query): Smart in-memory cache with automatic refetching
 * 2. Network Layer (Service Worker): Persistent disk cache for offline support
 *
 * React Query handles intelligent cache management and background refetching
 * Service Worker provides resilience and offline functionality
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache Time (gcTime): How long unused data stays in memory
      // 10 minutes - balances memory usage with reducing re-fetches
      gcTime: 10 * 60 * 1000,

      // Stale Time: How long data is considered fresh
      // 5 minutes default - suitable for IoT sensor data that updates periodically
      staleTime: 5 * 60 * 1000,

      // Retry Logic: Exponential backoff for network failures
      // 3 retries with delays: 1s, 2s, 4s
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch Strategies: Smart background updates
      refetchOnWindowFocus: true,  // Refetch when user returns to tab
      refetchOnReconnect: true,    // Refetch when network reconnects
      refetchOnMount: true,        // Refetch when component mounts

      // Network Mode: How to behave when offline
      // 'online' - only run queries when online (Service Worker handles offline)
      networkMode: 'online',

      // Error Handling
      throwOnError: false, // Don't throw errors globally, handle in components
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      retryDelay: 1000,

      // Network Mode for mutations
      networkMode: 'online',
    },
  },
})

/**
 * Query Key Factory
 * Centralized query key management for consistency and type safety
 */
export const queryKeys = {
  // User settings
  userUnits: ['user-units'] as const,

  // Devices
  devices: ['devices'] as const,
  devicesStatus: (deviceIds: string[]) => ['devices-status', deviceIds] as const,

  // Readings
  readings: (sigfoxId: string, from: number, to: number) =>
    ['readings', sigfoxId, from, to] as const,
  // Compare mode caches a different shape than the device view's windowed
  // readings, so it needs its own entry. The marker goes last so invalidating
  // ['readings', sigfoxId] still refreshes it.
  compareReadings: (sigfoxId: string, from: number, to: number) =>
    ['readings', sigfoxId, from, to, 'compare'] as const,
  batteryReadings: (sigfoxId: string, from: number, to: number) =>
    ['battery-readings', sigfoxId, from, to] as const,
  lastReadings: (sigfoxId: string) => ['last-readings', sigfoxId] as const,

  // Sensor configurations
  sensorConfigs: (sigfoxId: string, timestamp: number, showAllSensors?: boolean) =>
    ['sensor-configs', sigfoxId, timestamp, showAllSensors] as const,
  deviceConfig: (sigfoxId: string) => ['device-config', sigfoxId] as const,

  // Alert settings
  alertSettings: (deviceId?: number) =>
    deviceId ? ['alert-settings', deviceId] as const : ['alert-settings'] as const,
  alertControls: ['alert-controls'] as const,
  hysteresisSettings: ['hysteresis-settings'] as const,

  // Sensor-specific
  sensorPosition: (deviceId: number, sensorId: number) =>
    ['sensor-position', deviceId, sensorId] as const,
  sensorThresholds: (deviceId: number, sensorId: number) =>
    ['sensor-thresholds', deviceId, sensorId] as const,
  sensorAlerts: (deviceId: number, sensorId: number) =>
    ['sensor-alerts', deviceId, sensorId] as const,

  // Downlink remote-control
  controlCapabilities: (sigfoxId: string) => ['control-capabilities', sigfoxId] as const,
  deviceCommands: (sigfoxId: string) => ['device-commands', sigfoxId] as const,

  // Raw messages
  rawConfigMessages: (sigfoxId: string) => ['raw-config-messages', sigfoxId] as const,
  rawSigfoxMessages: (
    sigfoxId: string,
    to?: number,
    from?: number,
    order?: string,
    messageTypes?: string[]
  ) => ['raw-sigfox-messages', sigfoxId, to, from, order, messageTypes] as const,
} as const
